import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  stat,
  statRow,
  button,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { h2, caption, ticker, themeColors } from "./shared";
import { ReservoirSampler } from "../core/reservoir-sampling";
import { mulberry32 } from "../core/rng";
import { formatCount, formatPercent } from "../ui/format";
import type { PageRenderer } from "../ui/router";

export const renderReservoir: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Sampling · one pass, O(k) memory",
      title: "Reservoir sampling",
      lede: "Keep a uniform random sample of k items from a stream whose length you don't know — in a single pass, holding only k items. Every element seen so far is equally likely to be in your sample.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "The first k items just fill the reservoir. When the n-th item arrives (n > k), keep it with probability ",
        el("strong", {}, "k/n"),
        ", and if you keep it, evict a random current member. A one-line induction shows that after any number of items, each one seen is in the reservoir with probability exactly k/n — a genuinely uniform sample, no matter how the stream is ordered or how long it runs.",
      ),
    ),
  );

  liveCard(outlet);
  uniformityCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { ReservoirSampler } from "./core";

const rs = new ReservoirSampler(1000); // keep 1000 samples
for (const event of endlessStream) rs.add(event);

rs.sample(); // 1000 items, each a uniform draw from everything seen`,
    ),
  );

  append(
    outlet,
    callout(
      "tip",
      el(
        "div",
        {},
        el("strong", {}, "Where it shows up. "),
        "Sampling log lines under load, picking a representative subset for a dashboard, A/B bucketing, or streaming any dataset too large to shuffle. Vitter's Algorithm L makes it even faster by jumping ahead to the next item that will be admitted instead of rolling the dice on every one.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function liveCard(outlet: HTMLElement): void {
  const kSig = signal(8);
  const { tick, bump } = ticker();
  let rng = mulberry32(1);
  let rs = new ReservoirSampler<number>(kSig.value, rng);
  let lastAdmitted = -1;
  const admitLog: Array<[number, number]> = []; // (n, admit probability)

  function reset(): void {
    rng = mulberry32((Math.random() * 1e9) | 0);
    rs = new ReservoirSampler<number>(kSig.value, rng);
    lastAdmitted = -1;
    admitLog.length = 0;
    bump();
  }

  // Rebuild when k changes.
  effect(() => {
    kSig.value;
    reset();
  });

  function feed(count: number): void {
    for (let i = 0; i < count; i++) {
      const idx = rs.seen; // this item's stream position (0-based)
      const admitted = rs.add(idx);
      if (admitted) lastAdmitted = idx;
      if (rs.seen % Math.max(1, Math.floor(count / 20)) === 0 || count <= 40) {
        admitLog.push([rs.seen, rs.nextAdmitProbability()]);
      }
    }
    bump();
  }

  const slotsHost = el("div", { class: "viz-wrap" });
  const chartHost = el("div", { class: "viz-wrap" });

  effect(() => {
    tick.value;
    const held = rs.sample();
    slotsHost.replaceChildren(
      el(
        "div",
        { class: "reservoir" },
        ...held.map((v) =>
          el("span", { class: `res-slot${v === lastAdmitted ? " hot" : ""}` }, `#${v}`),
        ),
        ...Array.from({ length: Math.max(0, kSig.value - held.length) }, () =>
          el("span", { class: "res-slot empty" }, "·"),
        ),
      ),
    );

    chartHost.replaceChildren(
      plot({
        height: 240,
        xScale: "log",
        yScale: "log",
        xLabel: "items seen (n)",
        yLabel: "P(admit next item) = k/n",
        xFormat: formatCount,
        yFormat: (v) => formatPercent(v),
        series: [{ type: "line", points: admitLog.length ? admitLog : [[kSig.value, 1]], color: PALETTE[0], label: "k / n" }],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({ label: "reservoir size k", min: 2, max: 24, signal: kSig, format: (v) => String(v) }),
    el(
      "div",
      { class: "button-row" },
      button({ label: "+1", variant: "ghost", onClick: () => feed(1) }),
      button({ label: "+100", variant: "ghost", onClick: () => feed(100) }),
      button({ label: "+10K", variant: "primary", onClick: () => feed(10000) }),
      button({ label: "Reset", variant: "ghost", onClick: reset }),
    ),
    statRow(
      stat({ label: "Items seen", value: () => (tick.value, formatCount(rs.seen)) }),
      stat({ label: "Holding", value: () => (tick.value, `${rs.sample().length} / ${kSig.value}`) }),
    ),
    stat({
      label: "Chance the next item is kept",
      accent: true,
      value: () => (tick.value, formatPercent(rs.nextAdmitProbability())),
      hint: "starts at 100%, falls as k/n",
    }),
    el("p", { class: "mini-note" }, "Each slot shows the stream position it currently holds. As the stream grows, admissions get rarer — but the sample stays uniform."),
  );

  const viz = el(
    "div",
    {},
    slotsHost,
    caption("The reservoir holds k stream positions. A freshly admitted item flashes; older ones survive with ever-higher odds."),
    chartHost,
  );

  append(
    outlet,
    card({
      title: "Watch a sample form",
      subtitle: "Feed the stream and watch the reservoir fill, then slowly churn as admissions become rarer.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}

// ---------------------------------------------------------------------------

function uniformityCard(outlet: HTMLElement): void {
  const N = 100;
  const kSig = signal(5);
  const { tick, bump } = ticker();
  const counts = new Int32Array(N);
  let trials = 0;
  let seed = 12345;

  function run(batch: number): void {
    const k = kSig.value;
    for (let t = 0; t < batch; t++) {
      const rs = new ReservoirSampler<number>(k, mulberry32(seed++));
      for (let i = 0; i < N; i++) rs.add(i);
      for (const v of rs.sample()) counts[v]++;
    }
    trials += batch;
    bump();
  }

  function reset(): void {
    counts.fill(0);
    trials = 0;
    bump();
  }

  effect(() => {
    kSig.value;
    reset();
  });

  const chartHost = el("div", { class: "viz-wrap" });
  effect(() => {
    tick.value;
    const expected = trials > 0 ? (trials * kSig.value) / N : 0;
    const bars: Array<[number, number]> = [];
    for (let i = 0; i < N; i++) bars.push([i, counts[i]]);
    const c = themeColors();
    chartHost.replaceChildren(
      plot({
        height: 300,
        xLabel: "stream position (0…99)",
        yLabel: "times sampled",
        xFormat: (v) => String(v),
        yFormat: (v) => formatCount(v),
        series: [
          { type: "bar", points: bars, color: PALETTE[1], opacity: 0.7, label: "count" },
          { type: "line", points: [[0, expected], [N - 1, expected]], color: c.accent, dashed: true, label: "expected k·trials/N" },
        ],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({ label: "reservoir size k", min: 1, max: 20, signal: kSig, format: (v) => String(v) }),
    el(
      "div",
      { class: "button-row" },
      button({ label: "Run 2,000 trials", variant: "primary", onClick: () => run(2000) }),
      button({ label: "Reset", variant: "ghost", onClick: reset }),
    ),
    statRow(
      stat({ label: "Trials", value: () => (tick.value, formatCount(trials)) }),
      stat({ label: "Samples/position", accent: true, value: () => (tick.value, trials ? ((trials * kSig.value) / N).toFixed(0) : "—") }),
    ),
    el("p", { class: "mini-note" }, "Sampling k from a length-100 stream, over and over. Every position should be picked about equally often — the bars flatten toward the dashed line."),
  );

  append(
    outlet,
    card({
      title: "It's genuinely uniform",
      subtitle: "Run thousands of trials and tally how often each stream position ends up in the sample. No position is favoured.",
      children: [el("div", { class: "lab-layout" }, controls, chartHost)],
    }),
  );
}
